import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useSlug } from '@/hooks/useSlug'
import { type JobFormData, JobStatus, jobWithRealationsSchema } from '@/types/jobs'
import type { Addresses, Customers, Jobs } from '@/types/pbSchema'

export const defaultJobFormValues: JobFormData = {
    identifier: '',
    name: '',
    notes: '',
    status: JobStatus.ACTIVE,
    started: '',
    completed: '',
    tags: [],
    customer: { name: '', identifier: '' },
    expand: {
        address: {
            line1: '',
            line2: '',
            city: '',
            stateProvinceCounty: '',
            zipOrPostcode: '',
            country: '',
        },
    },
}

export const jobFormConfig = {
    mode: 'onChange' as const,
    resolver: zodResolver(jobWithRealationsSchema),
}

export interface UseJobFormOptions {
    job?: Jobs
    customer?: Customers
    tags?: string[]
    address?: Addresses
}

export function useJobForm({ job, customer, tags, address }: UseJobFormOptions = {}) {
    const form = useForm<JobFormData>({
        ...jobFormConfig,
        defaultValues: defaultJobFormValues,
    } as Parameters<typeof useForm<JobFormData>>[0])

    const { reset, watch, setValue, handleSubmit, formState } = form

    const onSubmitRef = useRef<((data: JobFormData) => void | Promise<void>) | null>(null)

    useSlug<JobFormData>({ watch, setValue })

    useEffect(() => {
        if (job) {
            reset({
                ...job,
                customer: customer ?? { name: '', identifier: '' },
                tags: tags ?? job.tags ?? [],
                started: job.started || undefined,
                completed: job.completed || undefined,
                expand: {
                    address: address ?? {
                        line1: '',
                        line2: '',
                        city: '',
                        stateProvinceCounty: '',
                        zipOrPostcode: '',
                        country: '',
                    },
                },
            } as JobFormData)
        }
    }, [job, customer, tags, address, reset])

    const handleFormSubmit = handleSubmit(
        async data => await onSubmitRef.current?.(data as JobFormData)
    )

    return {
        ...form,
        handleSubmit: handleFormSubmit,
        onSubmitRef,
        isSubmitting: formState.isSubmitting,
    }
}
